import { useEffect, useMemo, useState } from 'react';
import {
  PendingAdjustmentSummary,
  StockAdjustmentService,
} from '../api/stock-adjustment.service';

const service = new StockAdjustmentService();

/**
 * Pending (not yet approved) stock adjustments for the given products, keyed
 * by product id. The shown stock is the approved figure; this lets the product
 * screens say that a change is waiting (APPOINTMENT_FIXES_REPORT.md §5.3 S2).
 *
 * `refreshKey` re-fetches, e.g. after an adjustment is submitted.
 */
export default function usePendingAdjustments(
  productIds: (string | undefined)[],
  refreshKey?: unknown,
): Map<string, PendingAdjustmentSummary> {
  const [rows, setRows] = useState<PendingAdjustmentSummary[]>([]);
  const idsKey = useMemo(
    () => productIds.filter(Boolean).sort().join(','),
    [productIds],
  );

  useEffect(() => {
    if (!idsKey) {
      setRows([]);
      return undefined;
    }
    let cancelled = false;
    service
      .getPendingSummary(idsKey.split(','))
      .then(({ data }) => {
        if (!cancelled) setRows(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setRows([]);
      });
    return () => {
      cancelled = true;
    };
  }, [idsKey, refreshKey]);

  return useMemo(() => new Map(rows.map((r) => [r.productId, r])), [rows]);
}
